import { localStore, readSetting, writeSetting, type SettingsStore } from "./settings.ts";

/**
 * The viewer's reduced motion choice (R-0061, the author's request of 2026-09-28): System follows the device's
 * `prefers-reduced-motion`, On and Off override it. The stored text is the value.
 */
export type ReducedMotionChoice = "system" | "on" | "off";

/**
 * The storage key the reduced motion choice lives under.
 */
export const REDUCED_MOTION_KEY = "odyssey.reducedMotion";

/**
 * Reads a stored value as the reduced motion choice.
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns The choice, or `system` for anything that is not exactly `on` or `off`.
 */
export function parseReducedMotion(raw: string | null): ReducedMotionChoice {
  return raw === "on" || raw === "off" ? raw : "system";
}

/**
 * Reads the viewer's reduced motion choice.
 *
 * @param store Where it is kept; this browser's `localStorage` by default.
 * @returns The stored choice, or `system` when none is stored, it is not one of the choices or the store cannot be read.
 */
export function readReducedMotion(store: SettingsStore | null = localStore()): ReducedMotionChoice {
  return readSetting(REDUCED_MOTION_KEY, parseReducedMotion, store);
}

/**
 * Keeps the viewer's reduced motion choice.
 *
 * @param choice The choice.
 * @param store Where to keep it; this browser's `localStorage` by default.
 * @returns True when the store took it, false when there is no store or it refused.
 */
export function writeReducedMotion(choice: ReducedMotionChoice, store: SettingsStore | null = localStore()): boolean {
  return writeSetting(REDUCED_MOTION_KEY, choice, store);
}

/**
 * The game's reduced motion (R-0061, the author's request of 2026-09-28): Settings offers System, which follows the
 * device's `prefers-reduced-motion` and its changes, and On and Off, which override it. The page holds one
 * `ReducedMotionSetting` (`page-motion.ts`), which every surface that honours reduced motion reads: the canvas
 * surfaces ask it each frame, and the style sheets read the choice it sets on the document root as
 * `data-reduced-motion`, a rule applying under `[data-reduced-motion="on"]` and under the media query unless the
 * root says `off`. A page that never makes the setting carries no attribute, so its sheets follow the device alone.
 */

/**
 * The media query the device's preference is read from.
 */
export const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/**
 * The document root's attribute holding the choice for the style sheets.
 */
export const REDUCED_MOTION_ATTRIBUTE = "data-reduced-motion";

/**
 * The part of a `MediaQueryList` the setting uses.
 */
export interface MotionQuery {
  readonly matches: boolean;
  addEventListener(type: "change", listener: (event: { readonly matches: boolean }) => void): void;
  removeEventListener(type: "change", listener: (event: { readonly matches: boolean }) => void): void;
}

/**
 * The part of the document root the setting marks.
 */
export interface MotionRoot {
  setAttribute(name: string, value: string): void;
}

/**
 * Reduced motion as a surface reads it: whether motion is reduced now.
 */
export interface ReducedMotion {
  reduced(): boolean;
}

/**
 * Whether motion is reduced for a choice and the device's preference.
 *
 * @param choice The viewer's choice.
 * @param device Whether the device asks for reduced motion.
 * @returns The device's answer on System, true on On and false on Off.
 */
export function resolveReducedMotion(choice: ReducedMotionChoice, device: boolean): boolean {
  return choice === "system" ? device : choice === "on";
}

/**
 * The page's reduced motion, as `HealthNumbersSetting` holds the health numbers: made from the choice the store holds
 * as the page loads, following the device's preference through one listener, and taking each choice Settings makes,
 * which applies at once and is kept per viewer.
 *
 * @example
 * ```ts
 * const motion = new ReducedMotionSetting(readReducedMotion(), { query: window.matchMedia(REDUCED_MOTION_QUERY), root: document.documentElement });
 * motion.setChoice("on");
 * motion.reduced();
 * ```
 */
export class ReducedMotionSetting implements ReducedMotion {
  private current: ReducedMotionChoice;
  private device: boolean;
  private readonly query: MotionQuery | null;
  private readonly root: MotionRoot | null;
  private readonly listeners = new Set<() => void>();

  /**
   * Follows a change of the device's preference.
   *
   * @param event The query's change.
   */
  private readonly follow = (event: { readonly matches: boolean }): void => {
    const was = this.reduced();
    this.device = event.matches;
    this.tell(was);
  };

  /**
   * @param choice The choice the page is made with, as `readReducedMotion` gives it.
   * @param host The device's media query, or null where the page has none (System then reads as full motion), and the
   *   document root to mark, or null for none.
   * @param store Where a new choice is kept; this browser's `localStorage` when omitted.
   */
  constructor(
    choice: ReducedMotionChoice,
    host: { readonly query: MotionQuery | null; readonly root: MotionRoot | null },
    private readonly store?: SettingsStore | null,
  ) {
    this.current = choice;
    this.query = host.query;
    this.root = host.root;
    this.device = host.query?.matches ?? false;
    host.query?.addEventListener("change", this.follow);
    this.root?.setAttribute(REDUCED_MOTION_ATTRIBUTE, choice);
  }

  /**
   * The viewer's choice now.
   *
   * @returns The choice.
   */
  get choice(): ReducedMotionChoice {
    return this.current;
  }

  /**
   * Whether motion is reduced now, for the choice and the device's latest answer.
   *
   * @returns True when every blink, flash and animation that honours reduced motion holds still.
   */
  reduced(): boolean {
    return resolveReducedMotion(this.current, this.device);
  }

  /**
   * Takes the viewer's choice, which every surface reads at once, and keeps it.
   *
   * @param choice The choice.
   */
  setChoice(choice: ReducedMotionChoice): void {
    const was = this.reduced();
    this.current = choice;
    this.root?.setAttribute(REDUCED_MOTION_ATTRIBUTE, choice);
    writeReducedMotion(choice, this.store);
    this.tell(was);
  }

  /**
   * Listens for changes of whether motion is reduced, by a choice or by the device, so a surface that times its
   * animations from the moment they start (the player card's low flash) can start them again in phase.
   *
   * @param listener Called after each change.
   * @returns A function that removes the listener.
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Tells the listeners when whether motion is reduced differs from what it was.
   *
   * @param was Whether motion was reduced before the change.
   */
  private tell(was: boolean): void {
    if (this.reduced() !== was) {
      for (const listener of this.listeners) {
        listener();
      }
    }
  }

  /**
   * Stops following the device's preference, which then holds its last answer.
   */
  stop(): void {
    this.query?.removeEventListener("change", this.follow);
  }
}

/**
 * Keeps a target told whether motion is reduced, now and at each change, as the HUD's store takes it.
 *
 * @param motion The reduced motion setting.
 * @param target What takes the answer.
 * @returns A function that stops the mirror.
 */
export function mirrorReducedMotion(motion: ReducedMotionSetting, target: { setReducedMotion(reduced: boolean): void }): () => void {
  target.setReducedMotion(motion.reduced());
  return motion.subscribe(() => target.setReducedMotion(motion.reduced()));
}

/**
 * The page's device preference and document root, or nulls where there is no window, so a module that makes a
 * setting from them loads under `bun test`, where System reads as full motion.
 *
 * @returns The media query for `prefers-reduced-motion`, and the document root.
 */
export function pageMotionHost(): { readonly query: MotionQuery | null; readonly root: MotionRoot | null } {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return { query: null, root: null };
  }
  return { query: typeof window.matchMedia === "function" ? window.matchMedia(REDUCED_MOTION_QUERY) : null, root: document.documentElement };
}
